import React, { useState } from "react";
import { useForm } from "@formspree/react";
import { useTranslation } from "react-i18next";
import {
  useFormValidation,
  VALIDATION_RULES,
} from "../../hooks/useFormValidation";
import "./AssociatedMembersForm.css";

function AssociatedMembersForm() {
  const [state] = useForm("xanyeljk");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { t } = useTranslation();

  // Use secure form validation
  const {
    formData,
    errors,
    isSubmitting,
    updateField,
    handleSubmit: handleSecureSubmit,
    resetForm,
  } = useFormValidation(
    {
      firstName: "",
      lastName: "",
      phone: "",
      message: "",
    },
    {
      firstName: { ...VALIDATION_RULES.name, required: 'This field is required' },
      lastName: { ...VALIDATION_RULES.name, required: 'This field is required' },
      phone: { 
        ...VALIDATION_RULES.phone, 
        required: 'This field is required',
        minLength: 7,
        pattern: /^[+]?[\d\s\-()]{7,20}$/,
        patternMessage: "Please enter a valid phone number (at least 7 digits)",
        validate: (value) => {
          if (!value || value.trim() === '') {
            return 'This field is required';
          }
          // Remove all non-digit characters to count actual digits
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length < 7) {
            return 'Phone number must contain at least 7 digits';
          }
          if (digitsOnly.length > 15) {
            return 'Phone number must not exceed 15 digits';
          }
          // Check the pattern
          if (!/^[+]?[\d\s\-()]{7,20}$/.test(value)) {
            return 'Please enter a valid phone number';
          }
          return true;
        }
      },
      message: { ...VALIDATION_RULES.message, required: 'This field is required' },
    }
  );

  // Display general errors if any
  const generalError = errors.general;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const success = await handleSecureSubmit(async (data) => {
      try {
        // Create FormData for Formspree
        const formDataToSubmit = new FormData();
        formDataToSubmit.append("firstName", data.firstName.trim());
        formDataToSubmit.append("lastName", data.lastName.trim());
        formDataToSubmit.append("phone", data.phone.trim());
        formDataToSubmit.append("message", data.message.trim());

        // Submit to Formspree
        const response = await fetch("https://formspree.io/f/xanyeljk", {
          method: "POST",
          body: formDataToSubmit,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Form submission failed");
        }

        // If successful, update local state
        setSubmitSuccess(true);
        resetForm();
      } catch (error) {
        console.error("Form submission error:", error);
        throw error;
      }
    });

    if (!success) {
      // Form validation failed - scroll to first error field
      setTimeout(() => {
        // Find the first field with an error by checking the errors object
        const fieldOrder = ['firstName', 'lastName', 'phone', 'message'];
        let firstErrorFieldName = null;
        
        for (const fieldName of fieldOrder) {
          if (errors[fieldName]) {
            firstErrorFieldName = fieldName;
            break;
          }
        }
        
        if (firstErrorFieldName) {
          // Find the form group for the first error field
          const fieldElement = document.getElementById(firstErrorFieldName);
          if (fieldElement) {
            const formGroup = fieldElement.closest('.form-group-associated');
            if (formGroup) {
              // Scroll to the form group with smooth behavior
              formGroup.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              
              // Focus the input/textarea after scrolling
              setTimeout(() => {
                fieldElement.focus();
              }, 300);
            }
          }
        }
      }, 100);
    }
  };

  if (state.succeeded || submitSuccess) {
    return (
      <p className="associated-success-message">{t("form.successMessage")}</p>
    );
  }

  return (
    <section>
      <div className="associated-header-container">
        <p className="associated-required-note">{t("contact_us_all_fields_required") || "All fields are mandatory"}</p>
      </div>
      <form
        onSubmit={handleSubmit}
        className="associated-form"
      >
        <div className="form-group-associated">
          <label htmlFor="firstName" className="associated-form-label">
            {t("form.firstNameLabel")} <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            id="firstName"
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={(e) => updateField("firstName", e.target.value)}
            placeholder={t("form.firstNamePlaceholder")}
            className={`required-field ${errors.firstName ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.firstName && (
            <span className="validation-error">
              {errors.firstName}
            </span>
          )}
        </div>

        <div className="form-group-associated">
          <label htmlFor="lastName" className="associated-form-label">
            {t("form.lastNameLabel")} <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            id="lastName"
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => updateField("lastName", e.target.value)}
            placeholder={t("form.lastNamePlaceholder")}
            className={`required-field ${errors.lastName ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <span className="validation-error">{errors.lastName}</span>
          )}
        </div>

        <div className="form-group-associated">
          <label htmlFor="phone" className="associated-form-label">
            {t("form.phoneLabel")} <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            placeholder={t("form.phonePlaceholder")}
            className={`required-field ${errors.phone ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.phone && (
            <span className="validation-error">{errors.phone}</span>
          )}
        </div>

        <div className="form-group-associated full-width">
          <label htmlFor="message" className="associated-form-label">
            {t("form.messageLabel")} <span style={{ color: 'red' }}>*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={(e) => updateField("message", e.target.value)}
            placeholder={t("form.messagePlaceholder")}
            className={`required-field ${errors.message ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.message && (
            <span className="validation-error">{errors.message}</span>
          )}
        </div>

        {generalError && (
          <div className="validation-error" style={{ marginTop: "0.5rem", padding: "0.5rem", backgroundColor: "#fef2f2", borderRadius: "4px", border: "1px solid #dc2626" }}>
            {generalError}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || state.submitting}
          className="submit-associated"
        >
          {isSubmitting ? t("form.submitting") : t("form.submitButton")}
        </button>
      </form>
    </section>
  );
}

export default AssociatedMembersForm;
